import type { AxiosInstance } from 'axios';
import authService from './auth.service';

/**
 * A client's trusted contact (owner, 2026-10-31): a person close to them whom QMI will recognise
 * if they get in touch about the client. No access to the account, no right over the money, and
 * QMI does not contact them on its own initiative. Not a designation of who receives the
 * client's money: there is no percentage. An e-mail (lower case), a phone (E.164, any country)
 * or both. The API keeps its historical `beneficiaries` name.
 */
export interface Beneficiary {
  id: string;
  full_name: string;
  relationship: string;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

/** At least one of `email` / `phone`; the server refuses a contact with neither. */
export interface AddBeneficiaryPayload {
  full_name: string;
  relationship: string;
  email?: string;
  phone?: string;
}

/** A field left out is unchanged; `email` / `phone` sent as null clear that one, as long as
 *  the other remains. */
export interface UpdateBeneficiaryPayload {
  full_name?: string;
  relationship?: string;
  email?: string | null;
  phone?: string | null;
  is_active?: boolean;
}

class BeneficiariesService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  /** Manager / agent / self-client: list a specific client's
   *  contacts. Backend enforces role+target matrix. */
  async listForClient(clientId: string): Promise<Beneficiary[]> {
    const res = await this.api.get<Beneficiary[]>(
      `/api/v1/clients/${clientId}/beneficiaries`,
    );
    return res.data;
  }

  /** Shortcut for the authenticated client. Internally resolves the
   *  caller's UUID via /auth/me (cached). */
  async listMine(): Promise<Beneficiary[]> {
    const id = await authService.getMyId();
    return this.listForClient(id);
  }

  async add(clientId: string, body: AddBeneficiaryPayload): Promise<{ id: string }> {
    const res = await this.api.post<{ id: string }>(
      `/api/v1/clients/${clientId}/beneficiaries`,
      body,
    );
    return res.data;
  }

  /** Self-service add for clients — resolves caller's UUID and uses
   *  the same /clients/{id}/beneficiaries endpoint. */
  async addMine(body: AddBeneficiaryPayload): Promise<{ id: string }> {
    const id = await authService.getMyId();
    return this.add(id, body);
  }

  /** Atomically add up to 3 contacts. Backend rejects when the active
   *  count would go over 3. Either all rows persist or none do. */
  async addBatch(
    clientId: string,
    beneficiaries: AddBeneficiaryPayload[],
  ): Promise<{ ids: string[] }> {
    const res = await this.api.post<{ ids: string[] }>(
      `/api/v1/clients/${clientId}/beneficiaries/batch`,
      { beneficiaries },
    );
    return res.data;
  }

  /** Self-service batch add for clients — resolves caller's UUID. */
  async addBatchMine(
    beneficiaries: AddBeneficiaryPayload[],
  ): Promise<{ ids: string[] }> {
    const id = await authService.getMyId();
    return this.addBatch(id, beneficiaries);
  }

  async update(beneficiaryId: string, body: UpdateBeneficiaryPayload): Promise<void> {
    await this.api.patch(`/api/v1/beneficiaries/${beneficiaryId}`, body);
  }

  /** Soft-delete (sets is_active=false). Row stays for audit. */
  async deactivate(beneficiaryId: string): Promise<void> {
    await this.api.post(`/api/v1/beneficiaries/${beneficiaryId}/deactivate`);
  }
}

export default new BeneficiariesService();
